// PapaDashboard.js — Complete rebuild (Papa Dashboard + Reports + Incentive Token Details)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { usePPCAuth, PPC_API_URL } from './PPCContext';
import PapaReportDashboard from './PapaReportDashboard';   // ADDED Turn 1: new analytics dashboard

// ── Constants ──────────────────────────────────────────────────────────────────
// Local-date string (avoids toISOString() rolling the date back in +UTC zones like IST)
const ymdLocal = (x) => `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
const TODAY = ymdLocal(new Date());
const STD_TOK = 15; // standard tokens per associate (Incentive Token Details tab); tokens above this are incentive
const EXTRA_TOK_STD = 16; // standard tokens per associate for Add Entry incentive calculation

const PRODUCTS = [
  'HLSO','CPTO','REZP','CEZP','RPND','CPND','RPTO','RTNC',
  'CIST','CIST-TG','CIST SR CM','REZP-TG','REZP-NC','HLSO-BT',
  'RTNC-TG','BT','VANNAMEI','SUSHI',
];
const GRADES = [
  '3L','4L','5L','6/8','8/12','13/15','16/20','21/25','26/30',
  '31/40','41/50','51/60','61/70','71/90','91/110','111/130','131/150','151/200',
];

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt  = (v, d = 2) => parseFloat(v || 0).toFixed(d);
const fmtD = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  const mn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(day)} ${mn[parseInt(m)-1]} ${y.slice(2)}`;
};
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const getPeriodRange = (date, per) => {
  if (!date) return { from: date, to: date };
  const d = new Date(date + 'T00:00:00');
  if (per === 'daily') return { from: date, to: date };
  if (per === 'weekly') {
    const day = d.getDay();
    const mon = new Date(d); mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    // use LOCAL date parts — toISOString() shifts to UTC and can roll back a day
    return { from: ymdLocal(mon), to: ymdLocal(sun) };
  }
  if (per === 'monthly') {
    const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
    const last = new Date(d.getFullYear(), d.getMonth()+1, 0);
    return { from, to: ymdLocal(last) };
  }
  return { from: date, to: date };
};

// ═══════════════════════════════════════════════════════════════════════════════
// CENTRALIZED CALCULATION ENGINE — single source of truth.
// Dashboard cards, table footer, reports, and Excel export ALL derive their
// numbers from aggregate(). No component computes totals on its own.
//   • Valid shifts: DAY, GENERAL, NIGHT — all included in every aggregation.
//   • Yield is ALWAYS derived from totals (Output / HL × 100), never stored.
//   • All readers are null / undefined / '' / NaN safe.
// ═══════════════════════════════════════════════════════════════════════════════
const SHIFTS = ['DAY', 'GENERAL', 'NIGHT'];

const num = (v) => { const n = typeof v === 'number' ? v : parseFloat(v); return Number.isFinite(n) ? n : 0; };
const int = (v) => { const n = Math.trunc(num(v)); return Number.isFinite(n) ? n : 0; };

// Canonical per-row field readers (resolve every DB/frontend alias in one place).
// `??` (not `||`) so a legitimate 0 is never treated as "missing".
const rowOutputQty = (r) => num(r.output_qty ?? r.output_hl_qty ?? r.hon_qty ?? r.total_hon_quantity);
const rowHlQty     = (r) => num(r.hl_qty ?? r.input_qty ?? r.total_hl_quantity);
const rowFemale    = (r) => int(r.female_assoc ?? r.peeling_associates ?? r.female_supporting_associates);
// Issue 2: read supporting male & female from their OWN fields. The old code
// fell back to supporting_associates (the male+female SUM) for the male column,
// which made all support show under SUPP MALE and SUPP FEMALE read 0.
// supporting_associates is kept only as a last-resort fallback for legacy API
// responses that predate the split columns.
const rowSuppMale  = (r) => int(r.supp_male   ?? r.supporting_male   ?? r.supporting_associates);
const rowSuppFemale= (r) => int(r.supp_female ?? r.supporting_female);
const rowTokens    = (r) => int(r.tokens);
const rowHours     = (r) => num(r.working_hours ?? r.hrs);
const rowShift     = (r) => { const s = String(r.shift ?? '').trim().toUpperCase(); return SHIFTS.includes(s) ? s : (s || 'DAY'); };

// Yield is derived, never stored. Yield = Output / Headless × 100.
// Returns 0 when headless (denominator) is 0 — no NaN/Infinity. Null-safe via num().
const calcYield = (hlQty, outputQty) => (hlQty > 0 ? (outputQty / hlQty) * 100 : 0);
const yieldColor = (y) => (y >= 85 ? '#059669' : y >= 70 ? '#d97706' : '#d32f2f');

// THE aggregator. Every total in the UI/export comes from this function.
const aggregate = (rows) => {
  const t = { lots: 0, outputQty: 0, hlQty: 0, femaleAssoc: 0, suppMale: 0,
              suppFemale: 0, tokens: 0, workHours: 0 };
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r) return;
    t.lots        += 1;
    t.outputQty   += rowOutputQty(r);
    t.hlQty       += rowHlQty(r);
    t.femaleAssoc += rowFemale(r);
    t.suppMale    += rowSuppMale(r);
    t.suppFemale  += rowSuppFemale(r);
    t.tokens      += rowTokens(r);
    t.workHours   += rowHours(r);
  });
  // Total Associates counts only the actual token-working female associates.
  // Supporting male/female are support staff and are NOT counted here (their raw
  // values remain in t.suppMale / t.suppFemale and are still shown in the table).
  t.totalAssoc = t.femaleAssoc;
  t.yield      = calcYield(t.hlQty, t.outputQty);
  return t;
};

// Per-contractor rollup built on the same engine (used by reports + incentives).
const aggregateByContractor = (rows) => {
  const names = [...new Set((Array.isArray(rows) ? rows : []).map(r => r && r.contractor).filter(Boolean))];
  return names.map((c) => {
    const rr = rows.filter(r => r && r.contractor === c);
    const t  = aggregate(rr);
    const hrs = t.lots > 0 ? t.workHours / t.lots : 0; // avg hrs/entry (legacy behaviour)
    return {
      contractor: c,
      hon: t.outputQty, hl: t.hlQty, tokens: t.tokens,
      femaleAssoc: t.femaleAssoc, supp_male: t.suppMale, supp_female: t.suppFemale,
      assoc: t.totalAssoc, hrs,
      yld: t.yield,                              // Output / HL — same as dashboard
      // Productivity = Total Headless (HL) ÷ Total Associates ÷ Total Working Hours.
      // Divide-by-zero safe (0 associates or 0 hours → 0).
      prod: (t.totalAssoc > 0 && t.workHours > 0) ? (t.hlQty / t.totalAssoc / t.workHours) : 0,
    };
  });
};

// ── CSS ────────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
:root{--papa:#ea580c;--grad:linear-gradient(135deg,#ea580c,#f97316);--bg:#f0f2f5;--border:#e2e6ea;--text:#1a1d23;--muted:#6b7280;}
.pw *{box-sizing:border-box;margin:0;padding:0}
.pw{font-family:'DM Sans',sans-serif;background:var(--bg);min-height:100vh;font-size:13px;padding-bottom:48px}
.pw-hdr{background:var(--grad);padding:0 20px;height:52px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:200;box-shadow:0 2px 14px rgba(234,88,12,.3)}
.pw-hdr-l,.pw-hdr-r{display:flex;align-items:center;gap:10px}
.pw-hbtn{background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.28);color:#fff;padding:5px 12px;border-radius:6px;cursor:pointer;font-size:12px;font-family:inherit;font-weight:500}
.pw-hbtn:hover{background:rgba(255,255,255,.25)}
.pw-htitle{color:#fff;font-size:15px;font-weight:700}
.pw-hbadge{background:rgba(255,255,255,.2);color:#fff;font-size:10px;padding:3px 9px;border-radius:20px}
.pw-hdate{background:rgba(255,255,255,.95);border:none;border-radius:6px;padding:5px 10px;font-size:12px;font-family:inherit;color:var(--text);cursor:pointer;outline:none}
.pw-nav{background:#fff;border-bottom:2px solid var(--border);padding:0 20px;display:flex;position:sticky;top:52px;z-index:100;box-shadow:0 1px 4px rgba(0,0,0,.05)}
.pw-ntab{padding:11px 18px;font-size:12px;font-weight:600;border:none;background:transparent;color:var(--muted);cursor:pointer;border-bottom:3px solid transparent;font-family:inherit;transition:all .18s;display:flex;align-items:center;gap:6px;white-space:nowrap}
.pw-ntab.ap{color:#ea580c;border-bottom-color:#ea580c}
.pw-ntab.ar{color:#1565c0;border-bottom-color:#1565c0}
.pw-ntab.ai{color:#059669;border-bottom-color:#059669}
.pw-main{padding:14px 20px;display:flex;flex-direction:column;gap:12px}
.pw-chip-p{display:inline-flex;align-items:center;gap:6px;background:#ffedd5;color:#7c2d12;border:1px solid #fdba74;font-size:10px;font-weight:700;padding:3px 10px;border-radius:12px;letter-spacing:.4px}
.pw-chip-r{display:inline-flex;align-items:center;gap:6px;background:#dbeafe;color:#1e40af;border:1px solid #93c5fd;font-size:10px;font-weight:700;padding:3px 10px;border-radius:12px;letter-spacing:.4px}
.pw-chip-g{display:inline-flex;align-items:center;gap:6px;background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;font-size:10px;font-weight:700;padding:3px 10px;border-radius:12px;letter-spacing:.4px}
.pw-ptabs{display:flex;gap:4px;flex-wrap:wrap;align-items:center}
.pw-ptab{padding:8px 22px;border-radius:8px;border:1.5px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;transition:all .18s}
.pw-ptab.on{background:var(--grad);color:#fff;border-color:transparent;font-weight:700;box-shadow:0 3px 10px rgba(234,88,12,.25)}
.pw-range{font-size:11px;color:var(--muted);padding:6px 12px;background:#fff;border-radius:7px;border:1px solid var(--border)}
.pw-stats{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
.pw-sc{background:#fff;border:1.5px solid var(--border);border-radius:12px;padding:14px 16px;transition:box-shadow .18s}
.pw-sc:hover{box-shadow:0 4px 14px rgba(0,0,0,.09)}
.pw-sc-lbl{font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px}
.pw-sc-val{font-size:17px;font-weight:700;font-family:'DM Mono',monospace}
.pw-sc-sub{font-size:10px;margin-top:3px}
.kpi-a{border-left:4px solid #fdba74!important;background:#fff7ed!important}
.kpi-g{border-left:4px solid #86efac!important;background:#f0fdf4!important}
.kpi-b{border-left:4px solid #bfdbfe!important;background:#eff6ff!important}
.kpi-y{border-left:4px solid #fcd34d!important;background:#fffbeb!important}
.kpi-r{border-left:4px solid #fca5a5!important;background:#fff5f5!important}
.pw-card{background:#fff;border:1px solid var(--border);border-radius:12px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.04)}
.pw-chdr{padding:11px 16px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid var(--border);background:#fafbfc;flex-wrap:wrap;gap:8px}
.pw-ctitle{font-size:13px;font-weight:600}
.pw-btn-add{padding:7px 15px;border-radius:7px;border:none;background:#059669;color:#fff;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer}
.pw-btn-exp{padding:6px 13px;border-radius:7px;border:none;background:var(--grad);color:#fff;font-family:inherit;font-size:12px;cursor:pointer}
.pw-bsm{padding:4px 9px;border:none;border-radius:5px;color:#fff;font-size:10px;font-weight:700;cursor:pointer;font-family:inherit}
.pw-tw{overflow-x:auto}
table.pw-tbl{width:100%;border-collapse:collapse;font-size:11px;min-width:750px}
table.pw-tbl thead th{padding:8px 10px;font-size:9px;font-weight:700;text-align:center;border:1px solid var(--border);white-space:nowrap;letter-spacing:.3px;text-transform:uppercase;background:#fafbfc;color:var(--muted);vertical-align:middle}
table.pw-tbl thead th.tl{text-align:left;padding-left:13px}
table.pw-tbl tbody tr{border-bottom:1px solid #f1f5f9;transition:background .12s}
table.pw-tbl tbody tr:hover{background:#fff7ed}
table.pw-tbl td{padding:8px 10px;text-align:center;vertical-align:middle}
table.pw-tbl td.tl{text-align:left;padding-left:13px;font-weight:600;font-size:12px}
table.pw-tbl tfoot td{background:#f0f2f5;border-top:2px solid var(--border);font-weight:700;font-size:11px;text-align:center;padding:8px 10px}
table.pw-tbl tfoot td.tl{text-align:left;padding-left:13px}
.pw-mono{font-family:'DM Mono',monospace;font-size:11px}
.vo{color:#ea580c;font-weight:600;font-family:'DM Mono',monospace}
.vg{color:#059669;font-weight:600;font-family:'DM Mono',monospace}
.vb{color:#1565c0;font-weight:600;font-family:'DM Mono',monospace}
.va{color:#d97706;font-weight:600;font-family:'DM Mono',monospace}
.vr{color:#d32f2f;font-weight:600;font-family:'DM Mono',monospace}
.pw-insights{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.pw-ins{background:#fff;border:1px solid var(--border);border-radius:10px;padding:12px 16px;display:flex;align-items:center;gap:12px}
.pw-ins-ic{width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.pw-charts{display:grid;grid-template-columns:1fr 1fr 210px;gap:12px}
.pw-chrt{background:#fff;border:1px solid var(--border);border-radius:12px;overflow:hidden}
.pw-chrt-h{padding:10px 14px;border-bottom:1px solid var(--border);background:#fafbfc;font-size:12px;font-weight:600}
.pw-chrt-b{padding:12px 14px}
.pw-alert-badge{display:inline-flex;background:#fff5f5;color:#d32f2f;border:1px solid #fca5a5;padding:1px 6px;border-radius:10px;font-size:9px;font-weight:700;margin-left:4px;vertical-align:middle}
.pw-sync-badge{display:inline-flex;align-items:center;gap:5px;background:#fff7ed;color:#7c2d12;border:1px solid #fdba74;padding:3px 9px;border-radius:8px;font-size:10px;font-weight:600;margin-left:8px;vertical-align:middle}
.pw-sync-dot{width:6px;height:6px;border-radius:50%;background:#ea580c;display:inline-block;animation:ppulse 2s infinite}
@keyframes ppulse{0%,100%{opacity:1}50%{opacity:.3}}
.pw-empty{padding:48px;text-align:center;color:var(--muted)}
.pw-inc-card{border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(5,150,105,.12);border:1px solid #bbf7d0}
.pw-inc-strip{display:grid;grid-template-columns:repeat(5,1fr);background:#fff;border-bottom:1px solid #bbf7d0}
.pw-cbtn{display:flex;align-items:center;gap:7px;padding:8px 15px;border-radius:9px;border:1.5px solid #bbf7d0;background:#fff;cursor:pointer;font-family:inherit;font-size:12px;font-weight:500;color:var(--text);transition:all .18s}
.pw-cbtn.on{border-color:#059669;background:linear-gradient(135deg,#ecfdf5,#dbeafe);color:#059669;font-weight:700;box-shadow:0 2px 10px rgba(5,150,105,.15)}
.pw-cbadge{background:#059669;color:#fff;font-size:9px;font-weight:700;padding:1px 6px;border-radius:8px}
.pw-sum-row{display:grid;grid-template-columns:repeat(5,1fr);padding:14px 16px;background:linear-gradient(135deg,#f0fdf4,#eff6ff);border-bottom:1px solid #bbf7d0;gap:14px}
.pw-extra{display:inline-flex;background:linear-gradient(135deg,#ecfdf5,#d1fae5);border:1.5px solid #6ee7b7;color:#065f46;border-radius:20px;padding:4px 14px;font-size:13px;font-weight:700;font-family:'DM Mono',monospace}
.pw-no-inc{display:inline-flex;background:#f9fafb;border:1px solid #e5e7eb;color:#9ca3af;border-radius:20px;padding:4px 12px;font-size:12px}
.pw-tog{display:inline-flex;align-items:center;gap:6px;padding:8px 18px;margin:12px 16px;border-radius:8px;cursor:pointer;font-family:inherit;font-size:12px;font-weight:700}
.pw-tog-show{border:1.5px solid #6ee7b7;background:#ecfdf5;color:#059669}
.pw-tog-hide{border:1.5px solid transparent;background:linear-gradient(135deg,#059669,#1565c0);color:#fff}
.pw-prog-bar{flex:1;height:7px;background:#e5e7eb;border-radius:10px;overflow:hidden;min-width:60px}
.pw-prog-fill{height:100%;background:linear-gradient(90deg,#059669,#34d399);border-radius:10px}
.pw-ext-tag{background:#ecfdf5;border:1px solid #6ee7b7;color:#065f46;border-radius:12px;padding:2px 10px;font-size:11px;font-weight:700;font-family:'DM Mono',monospace}
.pw-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:500;display:flex;align-items:flex-start;justify-content:center;padding:20px;overflow-y:auto}
.pw-mbox{background:#fff;border-radius:16px;width:100%;max-width:680px;margin:auto;box-shadow:0 24px 64px rgba(0,0,0,.28);max-height:95vh;overflow-y:auto}
.pw-mhdr{background:var(--grad);padding:15px 20px;border-radius:16px 16px 0 0;display:flex;justify-content:space-between;align-items:flex-start;position:sticky;top:0;z-index:10}
.pw-mtitle{color:#fff;font-size:15px;font-weight:700}
.pw-msub{color:rgba(255,255,255,.75);font-size:11px;margin-top:3px}
.pw-mcls{width:28px;height:28px;border-radius:50%;border:none;background:rgba(255,255,255,.2);color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pw-mbody{padding:18px;display:grid;gap:13px}
.pw-mg2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.pw-mg3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px}
.pw-mg4{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:10px}
.pw-mg5{display:grid;grid-template-columns:1fr 1fr 1fr 1fr 1fr;gap:10px}
.pw-fsec-o{border-radius:10px;padding:13px;background:#fff7ed;border:1px solid #fdba74}
.pw-fsec-g{border-radius:10px;padding:13px;background:#f0fdf4;border:1px solid #6ee7b7}
.pw-fsec-c{border-radius:10px;padding:13px;background:#f8f9fa;border:1px solid var(--border)}
.pw-flbl-o{font-size:11px;font-weight:700;margin-bottom:10px;color:#ea580c}
.pw-flbl-g{font-size:11px;font-weight:700;margin-bottom:10px;color:#059669}
.pw-flbl-c{font-size:11px;font-weight:700;margin-bottom:10px;color:#6b7280}
.pw-ff label{display:block;font-size:10px;font-weight:700;color:var(--muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.4px}
.pw-ff input,.pw-ff select{width:100%;padding:9px 11px;border-radius:7px;border:1.5px solid var(--border);font-family:inherit;font-size:12px;background:#fff;color:var(--text);outline:none;transition:border-color .18s;box-sizing:border-box}
.pw-ff select{cursor:pointer}
.pw-ff input:focus,.pw-ff select:focus{border-color:#ea580c;box-shadow:0 0 0 3px rgba(234,88,12,.08)}
.pw-ff input.ro{background:#fff7ed;border-color:#fdba74;color:#ea580c;font-weight:600}
.pw-sbtn-wrap{display:flex;gap:8px}
.pw-sbtn{flex:1;padding:9px;border-radius:7px;border:2px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:11px;cursor:pointer;transition:all .18s}
.pw-sbtn.on{border-color:#ea580c;background:#ffedd5;color:#7c2d12;font-weight:700}
.pw-line-item{display:flex;gap:8px;align-items:flex-end;padding:10px;background:#fff;border-radius:8px;border:1px solid var(--border);margin-bottom:8px}
.pw-add-line{padding:6px 12px;border:1px solid #059669;border-radius:6px;background:#f0fdf4;color:#059669;font-family:inherit;font-size:12px;font-weight:600;cursor:pointer}
.pw-rm-line{padding:8px 10px;border:none;border-radius:6px;background:#fee2e2;color:#dc2626;font-weight:700;cursor:pointer;height:38px;align-self:flex-end}
.pw-calc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
.pw-calc-cell{background:#fff;border-radius:8px;padding:10px;border:1px solid var(--border);text-align:center}
.pw-calc-lbl{font-size:9px;color:var(--muted);text-transform:uppercase;letter-spacing:.3px;margin-bottom:4px}
.pw-calc-val{font-size:15px;font-weight:700;font-family:'DM Mono',monospace}
.pw-mfoot{padding:12px 18px;border-top:1px solid var(--border);display:flex;gap:9px;background:#fafbfc;border-radius:0 0 16px 16px;position:sticky;bottom:0}
.pw-btn-cancel{flex:1;padding:10px;border-radius:8px;border:1.5px solid var(--border);background:#fff;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer}
.pw-btn-save{flex:2;padding:10px;border-radius:8px;border:none;background:var(--grad);color:#fff;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer}
.pw-dbox{background:#fff;border-radius:16px;padding:30px 24px;width:100%;max-width:380px;margin:auto;text-align:center}
.pw-toast{position:fixed;bottom:22px;right:22px;padding:11px 18px;border-radius:9px;color:#fff;font-size:13px;font-weight:500;z-index:999;box-shadow:0 4px 18px rgba(0,0,0,.28);animation:pslide .22s ease}
@keyframes pslide{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
.pw-toast.ok{background:#059669}.pw-toast.err{background:#d32f2f}.pw-toast.warn{background:#d97706}
.pw-inc-card2{border-radius:12px;overflow:hidden;border:1.5px solid #6ee7b7;box-shadow:0 2px 12px rgba(5,150,105,.1)}
.pw-inc-grad-hdr{background:linear-gradient(135deg,#059669,#1565c0);padding:14px 18px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px}
.pw-inc-std-badge{background:rgba(234,88,12,.3);border:1px solid rgba(255,255,255,.3);border-radius:8px;padding:6px 14px;font-size:11px;font-weight:700;color:#fff;display:flex;align-items:center;gap:5px}
.pw-inc-filter-bar{background:#f0fdf4;border-bottom:1px solid #bbf7d0;padding:11px 16px;display:flex;gap:10px;align-items:flex-end;flex-wrap:wrap}
.pw-inc-filter-sel{padding:7px 10px;border-radius:7px;border:1.5px solid #6ee7b7;font-family:'DM Sans',sans-serif;font-size:12px;background:#fff;outline:none;min-width:150px;cursor:pointer;color:#1a1d23}
.pw-inc-clear{padding:7px 11px;border-radius:7px;border:1.5px solid #6ee7b7;background:#fff;color:#059669;font-family:inherit;font-size:12px;cursor:pointer;font-weight:600;display:flex;align-items:center;gap:4px}
.pw-stat-strip{display:grid;grid-template-columns:repeat(5,1fr);background:#fff;border-bottom:1px solid #bbf7d0}
.pw-stat-cell{padding:14px 16px}
.pw-stat-lbl{font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px;font-weight:600;display:flex;align-items:center;gap:5px}
.pw-stat-val{font-size:18px;font-weight:700;font-family:'DM Mono',monospace}
.pw-ctor-zone{background:#f0fdf4;border-bottom:1px solid #bbf7d0;padding:12px 16px}
.pw-ctor-zone-lbl{font-size:9px;font-weight:700;color:#059669;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;display:flex;align-items:center;gap:5px}
.pw-ctor-btns-wrap{display:flex;flex-wrap:wrap;gap:8px}
.pw-ctor-pill{display:flex;align-items:center;gap:7px;padding:7px 14px;border-radius:20px;border:1.5px solid #bbf7d0;background:#fff;cursor:pointer;font-family:inherit;font-size:12px;font-weight:500;color:var(--text);transition:all .18s}
.pw-ctor-pill.active{border-color:#059669;background:linear-gradient(135deg,#ecfdf5,#dbeafe);color:#059669;font-weight:700;box-shadow:0 2px 10px rgba(5,150,105,.15)}
.pw-ctor-earner{background:#059669;color:#fff;font-size:9px;font-weight:700;padding:1px 6px;border-radius:8px;min-width:18px;text-align:center}
.pw-det-panel{background:#fff}
.pw-det-summary{display:grid;grid-template-columns:repeat(5,1fr);padding:16px 18px;background:linear-gradient(135deg,#f0fdf4,#eff6ff);border-bottom:1px solid #bbf7d0;gap:12px}
.pw-det-cell{text-align:center}
.pw-det-lbl{font-size:9px;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px}
.pw-det-val{font-size:17px;font-weight:700;font-family:'DM Mono',monospace}
.pw-det-sub{font-size:10px;font-weight:600;margin-top:4px}
.pw-day-pill{background:#fef3c7;color:#e65100;padding:3px 9px;border-radius:8px;font-size:10px;font-weight:700;display:inline-block}
.pw-night-pill{background:#eff6ff;color:#1565c0;padding:3px 9px;border-radius:8px;font-size:10px;font-weight:700;display:inline-block}
.pw-extra-pill{display:inline-flex;background:linear-gradient(135deg,#ecfdf5,#d1fae5);border:1.5px solid #6ee7b7;color:#065f46;border-radius:20px;padding:5px 14px;font-size:14px;font-weight:700;font-family:'DM Mono',monospace}
.pw-no-inc-pill{display:inline-flex;background:#f9fafb;border:1px solid #e5e7eb;color:#9ca3af;border-radius:20px;padding:5px 12px;font-size:12px}
.pw-hide-tog{display:inline-flex;align-items:center;gap:6px;padding:9px 20px;margin:14px 18px;border-radius:8px;border:none;background:linear-gradient(135deg,#059669,#065f46);color:#fff;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer}
.pw-show-tog{display:inline-flex;align-items:center;gap:6px;padding:9px 20px;margin:14px 18px;border-radius:8px;border:1.5px solid #6ee7b7;background:#ecfdf5;color:#059669;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer}
.pw-emp-info-bar{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:9px 13px;font-size:11px;color:#1565c0;margin:0 18px 12px;display:flex;align-items:center;gap:6px}
.pw-emp-section{padding:0 18px 18px}
.pw-emp-full-tbl{width:100%;border-collapse:collapse;font-size:12px}
.pw-emp-full-tbl thead th{padding:10px 14px;font-size:10px;font-weight:700;background:#f8f9fa;border:1px solid var(--border);text-transform:uppercase;letter-spacing:.3px;color:var(--muted);white-space:nowrap}
.pw-emp-full-tbl thead th.ar{text-align:right}
.pw-emp-full-tbl tbody tr{border-bottom:1px solid #f1f5f9;transition:background .12s}
.pw-emp-full-tbl tbody tr:hover{background:#f0fdf4}
.pw-emp-full-tbl td{padding:11px 14px;vertical-align:middle}
.pw-emp-full-tbl td.ar{text-align:right}
.pw-rank-circle{display:inline-flex;width:26px;height:26px;border-radius:50%;color:#fff;font-size:11px;font-weight:700;align-items:center;justify-content:center}
.pw-ext-tag2{background:#ecfdf5;border:1px solid #6ee7b7;color:#065f46;border-radius:12px;padding:3px 10px;font-size:11px;font-weight:700;font-family:'DM Mono',monospace}
.pw-prog-wrap{display:flex;align-items:center;gap:10px}
.pw-prog-bar2{flex:1;height:8px;background:#e5e7eb;border-radius:10px;overflow:hidden;min-width:80px}
.pw-prog-fill2{height:100%;background:linear-gradient(90deg,#059669,#34d399);border-radius:10px}
.pw-emp-tbl-foot{background:#f0f2f5;border-top:2px solid var(--border)}
.pw-no-inc-state{padding:40px;text-align:center}
.pw-shortfall{display:inline-flex;align-items:center;gap:6px;background:#fef3c7;color:#92400e;font-size:11px;font-weight:600;padding:7px 14px;border-radius:8px;margin-top:12px}
.pw-et-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:5px}
.pw-et-lbl{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:var(--text)}
.pw-yn-btn{display:flex;align-items:center;gap:6px;padding:6px 18px;border-radius:20px;border:2px solid var(--border);background:#fff;font-family:inherit;font-size:12px;font-weight:600;cursor:pointer;transition:all .15s;color:var(--muted)}
.pw-yn-yes.on{border-color:#059669;background:#d1fae5;color:#065f46}
.pw-yn-no.on{border-color:#dc2626;background:#fee2e2;color:#7f1d1d}
.pw-inc-wrap{border-radius:10px;overflow:hidden;border:1.5px solid #6ee7b7}
.pw-inc-top{background:linear-gradient(135deg,#f0fdf4,#eff6ff);padding:11px 14px;border-bottom:1px solid #6ee7b7;display:flex;align-items:center;gap:8px}
.pw-new-badge{background:#1565c0;color:#fff;font-size:9px;font-weight:700;padding:2px 7px;border-radius:10px;letter-spacing:.3px}
.pw-inc-info{background:#eff6ff;border:1px solid #bfdbfe;border-radius:7px;padding:8px 12px;font-size:11px;color:#1565c0;margin:10px 13px 0;display:flex;align-items:center;gap:5px}
.pw-inc-body{padding:12px 13px}
.pw-emp-tbl{width:100%;border-collapse:collapse;font-size:11px}
.pw-emp-tbl thead th{padding:8px 10px;background:#f0fdf4;border:1px solid #a7f3d0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;color:#065f46}
.pw-emp-tbl tbody tr{transition:background .1s}
.pw-emp-tbl tbody tr:hover{background:#f0fdf4}
.pw-emp-tbl td{padding:6px 8px;border-bottom:1px solid #d1fae5;vertical-align:middle}
.pw-tinp{width:100%;padding:5px 8px;border-radius:5px;border:1.5px solid #d1fae5;font-family:'DM Mono',monospace;font-size:11px;outline:none;transition:border-color .15s;background:#fff;box-sizing:border-box}
.pw-tinp:focus{border-color:#059669}
.pw-tok-inp{text-align:right;width:80px}
.pw-del-x{width:24px;height:24px;border-radius:50%;border:1px solid #fecaca;background:#fee2e2;color:#dc2626;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;margin:0 auto}
.pw-add-emp{width:100%;padding:8px;border:1.5px dashed #6ee7b7;border-radius:7px;background:#f0fdf4;color:#059669;font-family:inherit;font-size:12px;font-weight:600;cursor:pointer;margin-top:8px}
.pw-add-emp:hover{background:#dcfce7}
.pw-tot-bar{background:#d1fae5;border:1px solid #6ee7b7;border-radius:8px;padding:10px 14px;display:flex;justify-content:space-between;align-items:center;font-weight:700;font-size:13px;color:#065f46;margin-top:10px}
.pw-tc-wrap{background:linear-gradient(135deg,#fff7ed,#eff6ff);border:1px solid #fdba74;border-radius:8px;padding:12px;margin-top:10px}
.pw-tc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
.pw-tc-cell{background:#fff;border-radius:8px;padding:9px;border:1px solid var(--border);text-align:center}
.pw-tc-lbl{font-size:9px;color:var(--muted);text-transform:uppercase;letter-spacing:.3px;margin-bottom:3px}
.pw-tc-val{font-size:15px;font-weight:700;font-family:'DM Mono',monospace}
.pw-msg-none{background:#fffbeb;border:1px solid #fcd34d;border-radius:7px;padding:8px 12px;font-size:11px;color:#92400e;margin-top:9px;display:flex;align-items:center;gap:5px}
.pw-msg-inc{background:#d1fae5;border:1px solid #6ee7b7;border-radius:7px;padding:8px 12px;font-size:11px;color:#065f46;margin-top:9px;display:flex;align-items:center;gap:5px}
@media(max-width:700px){.pw-stats{grid-template-columns:repeat(2,1fr)}.pw-charts{grid-template-columns:1fr}}
@media(max-width:700px){.pw-stats{grid-template-columns:repeat(2,1fr)}.pw-charts{grid-template-columns:1fr}}
`;

// ── Empty line item ────────────────────────────────────────────────────────────
const emptyLine = () => ({ product: '', grade: '', hl_qty: '', output_qty: '' });

// ── Component ──────────────────────────────────────────────────────────────────
export default function PapaDashboard() {
  const { user }  = usePPCAuth();
  const navigate  = useNavigate();
  const isAdmin   = user?.role === 'admin';

  // ── Tab & period ────────────────────────────────────────────────────────────
  const [activeTab,   setActiveTab]   = useState('papa');
  const [papaPeriod,  setPapaPeriod]  = useState('daily');
  const [rptPeriod,   setRptPeriod]   = useState('daily');
  const [selectedDate, setSelectedDate] = useState(TODAY);

  // ── Data ────────────────────────────────────────────────────────────────────
  const [entries,      setEntries]      = useState([]);
  const [contractors,  setContractors]  = useState(['DBS','KRISHNA','SAMUDRA','SHIVA','SHUKOOR LOCAL','SHUKOOR ISM','SONA','SONA CIST','SWF','VENKATESWARA']);
  const [loading,      setLoading]      = useState(false);
  const [apiOk,        setApiOk]        = useState(true);

  // ── Modal ───────────────────────────────────────────────────────────────────
  const [showModal,  setShowModal]  = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [fCtor,      setFCtor]      = useState('');
  const [fShift,     setFShift]     = useState('DAY');
  const [fTokens,    setFTokens]    = useState('');
  const [fAssocInc,  setFAssocInc]  = useState('');
  const [fMale,      setFMale]      = useState('');
  const [fFemale,    setFFemale]    = useState('');
  const [fFemaleAssoc, setFFemaleAssoc] = useState('');  // ✅ Female Associates
  const [fHrs,       setFHrs]       = useState('');
  const [fRemarks,   setFRemarks]   = useState('');
  const [lineItems,  setLineItems]  = useState([emptyLine()]);
  const [extraTokensOn, setExtraTokensOn] = useState(false);
  const [empRows,       setEmpRows]       = useState([{ id:1, name:'', empId:'', extra:'' }]);
  const [nextEmpId,     setNextEmpId]     = useState(2);

  // ── Delete ──────────────────────────────────────────────────────────────────
  const [showDel, setShowDel] = useState(false);
  const [delId,   setDelId]   = useState(null);

  // ── Incentive ───────────────────────────────────────────────────────────────
  const [selIncCtor,  setSelIncCtor]  = useState(null);
  const [showEmps,    setShowEmps]    = useState(true);
  const [empRecords,  setEmpRecords]  = useState({});
  // ADDED: state for inline edit + delete in incentive table
  const [editEmpKey, setEditEmpKey]   = useState(null);          // key of row currently being edited
  const [editEmpDraft, setEditEmpDraft] = useState({ name:'', id:'', tokens:0 });
  const [incFilter,   setIncFilter]   = useState('');
  // FIX: cross-contractor filters for emp-token table (mirrors Bravo)
  const [incEmpDate,  setIncEmpDate]  = useState('');   // '' = all
  const [incEmpShift, setIncEmpShift] = useState('');   // '' | 'DAY' | 'NIGHT'
  const [incEmpCtor,  setIncEmpCtor]  = useState('');   // '' = all

  // ── Toast ───────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState({ msg:'', type:'ok', show:false });
  const toastTimer = useRef(null);

  // ── Chart refs ──────────────────────────────────────────────────────────────
  const rTrendRef = useRef(null); const rTrendCh = useRef(null);
  const rBarRef   = useRef(null); const rBarCh   = useRef(null);
  const rGaugeRef = useRef(null); const rGaugeCh = useRef(null);
  // FIX: bumps when window.Chart becomes available — re-runs chart effect once lib loads
  const [chartReady, setChartReady] = useState(0);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type, show: true });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(s => ({ ...s, show: false })), 3000);
  };

  // ── Fetch entries ────────────────────────────────────────────────────────────
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    // FIX F1: use from/to range — backend now supports it
    const monthStart = `${selectedDate.slice(0,7)}-01`;
    const monthEnd   = ymdLocal(new Date(parseInt(selectedDate.slice(0,4)), parseInt(selectedDate.slice(5,7)), 0));
    try {
      const res = await axios.get(
        `${PPC_API_URL}/papa.php?action=entries&from=${monthStart}&to=${monthEnd}`, auth()
      );
      if (res.data?.success) {
        // FIX F2: normalise API field names → frontend field names so all rendering works
        const normalised = (res.data.entries || []).map(e => ({
          ...e,
          // DB field → frontend alias mapping
          output_qty:       parseFloat(e.output_hl_qty || e.output_qty || 0),
          hl_qty:           parseFloat(e.input_qty     || e.hl_qty     || 0),
          hon_qty:          parseFloat(e.output_hl_qty || e.output_qty || e.hon_qty || 0),
          total_hl_quantity:parseFloat(e.input_qty     || e.hl_qty     || 0),
          total_hon_quantity:parseFloat(e.output_hl_qty|| e.output_qty || 0),
          // Issue 2: map from the dedicated supporting_male / supporting_female
          // columns. supporting_associates (the old SUM) is only a legacy fallback
          // for the MALE side so historical totals don't vanish; female no longer
          // borrows the sum.
          supp_male:        parseInt(e.supporting_male   ?? e.supp_male   ?? e.supporting_associates ?? 0),
          supp_female:      parseInt(e.supporting_female ?? e.supp_female ?? 0),
          female_assoc:     parseInt(e.peeling_associates || e.female_assoc || 0),
          peeling_associates: parseInt(e.peeling_associates || e.female_assoc || 0),
          working_hours:    parseFloat(e.working_hours || e.hrs || 0),
          hrs:              parseFloat(e.working_hours || e.hrs || 0),
        }));
        setEntries(normalised);
        setApiOk(true);
      }
    } catch (_) { setApiOk(false); showToast('Could not load entries — check network', 'err'); }
    finally { setLoading(false); }
  }, [selectedDate]);

  const fetchContractors = useCallback(async () => {
    try {
      const res = await axios.get(`${PPC_API_URL}/contractors.php?action=by-module&module=PAPA`, auth());
      if (res.data?.success && res.data.contractors?.length)
        setContractors(res.data.contractors.map(c => c.name));
    } catch (_) {}
  }, []);

  // ── API: fetch persisted per-employee token records (last 60 days) ────────
  // Pulls historical rows from ppc_papa_emp_tokens so the Incentive tab shows
  // the FULL dataset (old + new), not just newly entered rows.
  const fetchEmpDetails = useCallback(async () => {
    try {
      const to    = new Date();
      const from  = new Date(); from.setDate(to.getDate() - 60);
      const fromStr = from.toISOString().slice(0, 10);
      const toStr   = to.toISOString().slice(0, 10);

      const res = await axios.get(
        `${PPC_API_URL}/papa.php?action=emp-tokens&from=${fromStr}&to=${toStr}`,
        auth()
      );
      if (res.data?.success && Array.isArray(res.data.rows)) {
        // Group rows by contractor + dedupe (emp_id|date|shift). UI reads
        // empRecords[contractor] so we keep that exact shape.
        const seen = new Set();
        const grouped = {};
        for (const r of res.data.rows) {
          if (!r.emp_id || !r.date || !r.contractor) continue;
          const key = `${r.emp_id}|${r.date}|${r.shift || 'DAY'}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const tok   = parseInt(r.tokens) || 0;
          const extra = Math.max(0, tok - STD_TOK);
          const row   = {
            id:     r.emp_id,
            name:   r.emp_name || 'Unknown',
            tokens: tok,
            extra,
            date:   r.date,
            shift:  r.shift || 'DAY',
            recordId: r.id,   // ADDED: DB primary key for delete
          };
          if (!grouped[r.contractor]) grouped[r.contractor] = [];
          grouped[r.contractor].push(row);
        }
        setEmpRecords(grouped);
      }
    } catch (err) {
      console.error('Papa fetchEmpDetails error:', err);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchContractors(); }, [fetchContractors]);
  useEffect(() => { fetchEmpDetails(); }, [fetchEmpDetails]);
  // Incentive section is pinned to the selected date — clear the secondary
  // date filter when the top date changes so it never shows a stale value.
  useEffect(() => { setIncEmpDate(''); }, [selectedDate]);

  // ── Period-filtered data ────────────────────────────────────────────────────
  // Single filtering path. Date range is inclusive; ALL shifts (DAY/GENERAL/NIGHT)
  // are retained — no shift is ever silently dropped here.
  const getFiltered = useCallback((per) => {
    const { from, to } = getPeriodRange(selectedDate, per);
    return entries.filter(e => {
      const d = e && (e.entry_date || e.date);
      return d && d >= from && d <= to;
    });
  }, [entries, selectedDate]);

  // Per-contractor rollup via the centralized engine (reports + incentives).
  const getCtorSummary = useCallback((per) => aggregateByContractor(getFiltered(per)), [getFiltered]);

  // ── Charts (Reports tab) ─────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'report') return;
    const cs = getCtorSummary(rptPeriod);
    if (!cs.length) return;
    // FIX: poll for Chart.js if not yet loaded — was silently bailing forever
    if (!window.Chart) {
      const t = setTimeout(() => { if (window.Chart) setChartReady(c => c + 1); }, 250);
      return () => clearTimeout(t);
    }
    const labels = cs.map(r => r.contractor.substring(0,5).toUpperCase());
    const cHON = cs.map(r => r.hon), cHL = cs.map(r => r.hl);
    const totHON = cHON.reduce((s,v)=>s+v,0), totHL = cHL.reduce((s,v)=>s+v,0);
    // Yield = Output / HL × 100 (same definition as the dashboard cards & footer)
    const yld = calcYield(totHL, totHON);
    const gc = yieldColor(yld);

    if (rTrendCh.current) rTrendCh.current.destroy();
    if (rTrendRef.current) rTrendCh.current = new window.Chart(rTrendRef.current, {
      type: 'line',
      data: { labels, datasets: [
        { label:'Output Qty', data:cHON, borderColor:'#ea580c', backgroundColor:'rgba(234,88,12,.08)', tension:.4, fill:true, pointRadius:4, pointBackgroundColor:'#ea580c' },
        { label:'HL Qty',   data:cHL,  borderColor:'#059669', backgroundColor:'rgba(5,150,105,.08)',   tension:.4, fill:true, pointRadius:4, pointBackgroundColor:'#059669', borderDash:[5,3] },
      ]},
      options: { responsive:true, maintainAspectRatio:false,
        plugins:{ legend:{ display:true, position:'top', labels:{ font:{ size:10 }, boxWidth:12, padding:10 } } },
        scales:{ x:{ grid:{ display:false }, ticks:{ font:{ size:10 } } },
                 y:{ grid:{ color:'rgba(0,0,0,.04)' }, ticks:{ font:{ size:10 }, callback:v=>v>=1000?(v/1000).toFixed(1)+'k':v } } } },
    });
    if (rBarRef.current) rBarRef.current.parentElement.style.height = Math.max(200, cs.length * 42 + 60) + 'px';
    if (rBarCh.current) rBarCh.current.destroy();
    if (rBarRef.current) rBarCh.current = new window.Chart(rBarRef.current, {
      type: 'bar',
      data: { labels: cs.map(r=>r.contractor), datasets: [
        { label:'Output Qty', data:cHON, backgroundColor:'rgba(234,88,12,.75)', borderRadius:4, borderSkipped:false },
        { label:'HL Qty',   data:cHL,  backgroundColor:'rgba(5,150,105,.75)',  borderRadius:4, borderSkipped:false },
      ]},
      options: { responsive:true, maintainAspectRatio:false, indexAxis:'y',
        plugins:{ legend:{ display:true, position:'top', labels:{ font:{ size:10 }, boxWidth:12, padding:10 } } },
        scales:{ x:{ grid:{ color:'rgba(0,0,0,.04)' }, ticks:{ font:{ size:9 }, callback:v=>v>=1000?(v/1000)+'k':v } },
                 y:{ grid:{ display:false }, ticks:{ font:{ size:10 } } } } },
    });
    if (rGaugeCh.current) rGaugeCh.current.destroy();
    if (rGaugeRef.current) rGaugeCh.current = new window.Chart(rGaugeRef.current, {
      type: 'doughnut',
      data: { datasets:[{ data:[yld, Math.max(0,100-yld)], backgroundColor:[gc,'#f0f2f5'], borderWidth:0 }] },
      options: { responsive:true, maintainAspectRatio:false, circumference:270, rotation:225, cutout:'72%',
        plugins:{ legend:{ display:false }, tooltip:{ enabled:false } } },
    });
    const lbl = document.getElementById('pw-gauge-lbl');
    if (lbl) { lbl.textContent = yld.toFixed(1) + '%'; lbl.style.color = gc; }
  }, [activeTab, rptPeriod, entries, selectedDate, chartReady]); // eslint-disable-line

  // ── Modal helpers ────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditId(null);
    setFCtor(''); setFShift('DAY'); setFTokens(''); setFAssocInc('');
    setFMale(''); setFFemale(''); setFFemaleAssoc(''); setFHrs(''); setFRemarks('');
    setLineItems([emptyLine()]);
    setExtraTokensOn(false);
    setEmpRows([{ id:1, name:'', empId:'', extra:'' }]);
    setNextEmpId(2);
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditId(e.id);
    setFCtor(e.contractor || '');
    setFShift(e.shift || 'DAY');
    setFTokens(e.tokens ?? '');
    setFAssocInc(e.assoc_earned_incentive ?? '');
    // FIX F6: read DB field names correctly (after normalisation in fetchEntries these are already aliased)
    setFMale(e.supp_male ?? e.supporting_male ?? '');
    setFFemale(e.supp_female ?? e.supporting_female ?? '');
    setFFemaleAssoc(e.female_assoc ?? e.peeling_associates ?? '');
    setFHrs(e.working_hours ?? e.hrs ?? '');
    setFRemarks(e.remarks || '');
    setLineItems([{
      product:    e.product || '',
      grade:      e.grade   || '',
      hl_qty:     e.hl_qty  ?? e.input_qty ?? '',
      output_qty: e.output_qty ?? e.output_hl_qty ?? '',
    }]);
    setExtraTokensOn(false);
    setEmpRows([{ id:1, name:'', empId:'', extra:'' }]);
    setNextEmpId(2);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditId(null); };

  const setLine = (idx, key, val) => setLineItems(prev => prev.map((l, i) => i === idx ? { ...l, [key]: val } : l));
  const addLine  = () => setLineItems(prev => [...prev, emptyLine()]);
  const remLine  = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  // ── Auto-calc ────────────────────────────────────────────────────────────────
  const calcOutput = lineItems.reduce((s, l) => s + (parseFloat(l.output_qty) || 0), 0);  // ✅ Output Qty
  const calcHL     = lineItems.reduce((s, l) => s + (parseFloat(l.hl_qty)     || 0), 0);
  const calcYld    = calcHL > 0 ? (calcOutput / calcHL * 100) : 0;  // Yield = Output / HL × 100
  const calcAssoc  = (parseInt(fMale)||0) + (parseInt(fFemale)||0) + (parseInt(fFemaleAssoc)||0);  // ✅ includes female assoc

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!fCtor) { showToast('Please select a contractor', 'err'); return; }
    if (!fShift) { showToast('Please select a shift', 'err'); return; }

    const firstLine = lineItems[0] || {};
    const totOutput = calcOutput, totHL = calcHL;
    const hrs = parseFloat(fHrs) || 0;
    const sm  = parseInt(fMale)   || 0;
    const sf  = parseInt(fFemale) || 0;
    const femaleAssocVal = parseInt(fFemaleAssoc) || 0;

    // FIX F3: use backend field names so PHP receives and saves correctly
    const payload = {
      entry_date:  selectedDate,
      contractor:  fCtor,
      shift:       fShift,
      tokens:      parseInt(fTokens) || 0,
      remarks:     fRemarks,
      working_hours: hrs,

      // Associates — send BOTH names for maximum compatibility
      peeling_associates:            femaleAssocVal,   // DB column name (female/peeling)
      supporting_associates:         sm + sf,          // DB column name (male+female support)
      female_assoc:                  femaleAssocVal,   // frontend alias
      supp_male:                     sm,
      supp_female:                   sf,
      total_associates:              sm + sf + femaleAssocVal,

      // Quantities — send BOTH names
      output_hl_qty: totOutput,   // DB column name
      output_qty:    totOutput,   // frontend alias
      input_qty:     totHL,       // DB column name
      hl_qty:        totHL,       // frontend alias

      product: firstLine.product || '',
      grade:   firstLine.grade   || '',
      yield_percent: calcYld,
      // Productivity = Headless (HL) ÷ Associates ÷ Working Hours.
      // e.g. HL 1200 ÷ 10 associates ÷ 8 hrs = 15. Divide-by-zero safe (→ 0).
      productivity:          (femaleAssocVal > 0 && hrs > 0) ? totHL / femaleAssocVal / hrs : 0,
      productivity_per_day:  femaleAssocVal > 0 ? totHL / femaleAssocVal : 0,
      productivity_per_hr:   (femaleAssocVal > 0 && hrs > 0) ? totHL / femaleAssocVal / hrs : 0,

      assoc_earned_incentive: parseInt(fAssocInc) || 0,
      extra_tokens_enabled:   extraTokensOn,
      extra_token_employees:  extraTokensOn ? empRows.map(r => ({
        name: r.name, emp_id: r.empId, extra_tokens: parseInt(r.extra) || 0,
      })) : [],
      total_extra_tokens: extraTokensOn ? empRows.reduce((s,r) => s + (parseInt(r.extra)||0), 0) : 0,
    };

    if (apiOk) {
      try {
        let res;
        if (editId) {
          res = await axios.put(`${PPC_API_URL}/papa.php?action=edit-entry`, { entry_id: editId, ...payload }, auth());
        } else {
          res = await axios.post(`${PPC_API_URL}/papa.php?action=entries`, payload, auth());
        }
        if (res.data?.success) {
          // Persist per-employee extra-token rows to dedicated table so they
          // survive reload. Append-only — never overwrites existing data.
          if (extraTokensOn) {
            const empPayload = empRows
              .filter(r => (r.name?.trim() || r.empId?.trim()) && (parseInt(r.extra) || 0) > 0)
              .map(r => ({
                date:       selectedDate,
                contractor: fCtor,
                shift:      fShift,
                emp_id:     r.empId?.trim() || `EMP-${Date.now()}`,
                emp_name:   r.name?.trim()  || 'Unknown',
                tokens:     STD_TOK + (parseInt(r.extra) || 0),
              }));
            if (empPayload.length > 0) {
              try {
                await axios.post(
                  `${PPC_API_URL}/papa.php?action=emp-tokens`,
                  { rows: empPayload },
                  auth()
                );
              } catch (err) {
                console.error('Papa emp-tokens save error:', err);
                showToast('Employee details: server sync failed', 'warn');
              }
            }
          }
          showToast(editId ? '✅ Entry updated!' : '✅ Entry saved!');
          closeModal();
          fetchEntries();
          fetchEmpDetails();
          return;
        }
        showToast(res.data?.error || 'Save failed', 'err');
      } catch (err) {
        showToast(err.response?.data?.error || 'Failed to save — check server', 'err');
        console.error('Papa save error:', err.response?.data || err);
      }
      return;
    }
    // Offline fallback
    if (editId) {
      setEntries(prev => prev.map(r => r.id === editId ? { ...r, ...payload } : r));
      showToast('Entry updated (offline)');
    } else {
      const newId = Math.max(0, ...entries.map(r => r.id || 0)) + 1;
      setEntries(prev => [...prev, { id: newId, ...payload }]);
      showToast('Entry saved (offline)');
    }
    closeModal();
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const openDel = (id) => { setDelId(id); setShowDel(true); };
  const closeDel = () => { setShowDel(false); setDelId(null); };
  const confirmDel = async () => {
    if (apiOk) {
      try {
        // FIX F4: use delete-entry with entry ID, not reset (which deletes ALL entries for a date)
        const res = await axios.delete(
          `${PPC_API_URL}/papa.php?action=delete-entry&id=${delId}`, auth()
        );
        if (res.data?.success) {
          showToast('Entry deleted', 'warn'); closeDel(); fetchEntries(); return;
        }
        showToast(res.data?.error || 'Delete failed', 'err');
      } catch (err) {
        // Fallback: remove locally if server errors
        console.error('Papa delete error:', err.response?.data || err);
      }
    }
    setEntries(prev => prev.filter(r => r.id !== delId));
    showToast('Entry deleted', 'warn'); closeDel();
  };

  // ── Export ───────────────────────────────────────────────────────────────────
  // Uses the SAME centralized engine as the dashboard: per-row readers for the
  // body and aggregate() for the TOTAL row, so the export can never diverge.
  const handleExport = () => {
    const per  = activeTab === 'report' ? rptPeriod : papaPeriod;
    const rows = getFiltered(per);
    const headers = ['Contractor','Date','Shift','Product','Grade','HL Qty (KG)','Output Qty (KG)','Female Assoc','Supp.Male','Supp.Female','Total Assoc','Tokens','Work Hrs'];
    const data = rows.map(r => {
      const hl    = rowHlQty(r);
      const outQt = rowOutputQty(r);
      const fa    = rowFemale(r);
      const sm    = rowSuppMale(r);
      const sf    = rowSuppFemale(r);
      return [r.contractor, r.entry_date||r.date||selectedDate, rowShift(r), r.product||'', r.grade||'',
              hl.toFixed(2), outQt.toFixed(2), fa, sm, sf, fa+sm+sf, rowTokens(r), rowHours(r)];
    });
    // TOTAL row — identical numbers to the dashboard cards & footer.
    const t = aggregate(rows);
    const totalRow = ['TOTAL ('+t.lots+')','','','','',
                      t.hlQty.toFixed(2), t.outputQty.toFixed(2), t.femaleAssoc, t.suppMale, t.suppFemale,
                      t.totalAssoc, t.tokens, t.workHours];
    const { from, to } = getPeriodRange(selectedDate, per);
    const fname = per === 'daily' ? selectedDate : `${from}_to_${to}`;
    try {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([
        ['PAPA Dashboard — '+fname],
        ['Yield % (Output / HL): '+t.yield.toFixed(2)+'%'],
        [], headers, ...data, [], totalRow,
      ]);
      ws['!cols'] = headers.map(()=>({ wch:14 }));
      XLSX.utils.book_append_sheet(wb, ws, 'Papa Entries');
      XLSX.writeFile(wb, `PAPA_${fname}.xlsx`);
      showToast('Exported!');
    } catch (_) { showToast('Export failed','err'); }
  };

  // Incentive Tokens come ONLY from the per-employee Extra Token entries
  // (ppc_papa_emp_tokens → empRecords), and ONLY for the selected date — no
  // accumulation across previous dates.
  const ctorEarners = (ctor) =>
    (empRecords[ctor] || []).filter(e => (parseInt(e.extra) || 0) > 0 && e.date === selectedDate);
  const ctorExtra = (ctor) =>
    ctorEarners(ctor).reduce((s, e) => s + Math.max(0, parseInt(e.extra || 0)), 0);

  const exportInc = () => {
    const cs = getCtorSummary('daily');
    const h = ['Contractor','Associates','Tokens Assigned','Tokens Achieved','Extra Tokens'];
    const d = cs.map(r => {
      // Associates = actual female associates doing token work (support excluded);
      // Standard = associates × 60; Incentive = Σ entered extra; Achieved = Std + Extra.
      const assoc    = parseInt(r.femaleAssoc) || 0;
      const ex       = ctorExtra(r.contractor);
      const std      = assoc * STD_TOK;
      const achieved = std + ex;
      return [r.contractor, assoc, std, achieved, ex > 0 ? '+'+ex : 0];
    });
    const csv = [h,...d].map(r=>r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `PAPA_Incentive_${selectedDate}.csv`;
    a.click();
    showToast('Exported!');
  };

  // ── Derived (memoized — single source of truth via the engine) ───────────────
  const papaRows = useMemo(() => getFiltered(papaPeriod), [getFiltered, papaPeriod]);
  const rptCS    = useMemo(() => getCtorSummary(rptPeriod), [getCtorSummary, rptPeriod]);
  const incCS    = useMemo(() => getCtorSummary('daily'),   [getCtorSummary]);

  // Dashboard totals — every card, the footer, and the export read from this.
  const totals = useMemo(() => aggregate(papaRows), [papaRows]);
  const kpi = useMemo(() => ({
    hon:   totals.outputQty,
    hl:    totals.hlQty,
    lots:  totals.lots,
    yld:   totals.yield,
    assoc: totals.totalAssoc,
    yC:    yieldColor(totals.yield),
  }), [totals]);

  const { from: ppFrom, to: ppTo } = getPeriodRange(selectedDate, papaPeriod);
  const ppRange = papaPeriod !== 'daily' ? `${fmtD(ppFrom)} — ${fmtD(ppTo)}` : '';
  const { from: rpFrom, to: rpTo } = getPeriodRange(selectedDate, rptPeriod);
  const rpRange = rptPeriod !== 'daily' ? `${fmtD(rpFrom)} — ${fmtD(rpTo)}` : '';

  // ── Incentive helpers ────────────────────────────────────────────────────────
  const getIncFiltered = () => {
    let rows = incCS;
    // "With incentive" = contractor has entered extra tokens; "none" = no extras.
    if (incFilter === 'inc')  rows = incCS.filter(r => ctorExtra(r.contractor) > 0);
    if (incFilter === 'none') rows = incCS.filter(r => ctorExtra(r.contractor) <= 0);
    return rows;
  };
  const incRows = getIncFiltered();
  const activeCtor = incRows.find(r => r.contractor === selIncCtor) || (incRows.length ? incRows[0] : null);

  // ── Period tab helper ────────────────────────────────────────────────────────
  const PeriodTabs = ({ value, onChange, range }) => (
    <div className="pw-ptabs">
      {['daily','weekly','monthly'].map(p => (
        <button key={p} className={`pw-ptab${value===p?' on':''}`} onClick={() => onChange(p)}>
          📅 {p.charAt(0).toUpperCase()+p.slice(1)}
        </button>
      ))}
      {range && <span className="pw-range">{range}</span>}
    </div>
  );

  // ADDED: Edit handler — start inline edit for a row
  const startEditEmp = (e, ctor) => {
    const key = `${ctor}|${e.id}|${e.date||''}|${e.shift||'DAY'}`;
    setEditEmpKey(key);
    setEditEmpDraft({ name: e.name||'', id: e.id||'', tokens: parseInt(e.tokens)||0 });
  };
  // ADDED: Edit handler — cancel
  const cancelEditEmp = () => { setEditEmpKey(null); setEditEmpDraft({ name:'', id:'', tokens:0 }); };
  // ADDED: Edit handler — save (POST upserts via existing emp-tokens endpoint)
  const saveEditEmp = async (e, ctor) => {
    const tokensNum = parseInt(editEmpDraft.tokens)||0;
    if (!editEmpDraft.id?.trim() || !editEmpDraft.name?.trim() || tokensNum < 0) {
      showToast('Name, ID, and non-negative tokens required', 'err'); return;
    }
    // Optimistic local update
    setEmpRecords(prev => {
      const list = (prev[ctor]||[]).map(row =>
        (row.id===e.id && row.date===e.date && (row.shift||'DAY')===(e.shift||'DAY'))
          ? { ...row, name: editEmpDraft.name.trim(), id: editEmpDraft.id.trim(),
              tokens: tokensNum, extra: Math.max(0, tokensNum - STD_TOK) }
          : row
      );
      return { ...prev, [ctor]: list };
    });
    // Persist to backend
    try {
      await axios.post(`${PPC_API_URL}/papa.php?action=emp-tokens`, {
        rows: [{ date: e.date, contractor: ctor, shift: e.shift||'DAY',
                 emp_id: editEmpDraft.id.trim(), emp_name: editEmpDraft.name.trim(),
                 tokens: tokensNum }]
      }, auth());
      showToast('✅ Updated');
    } catch (err) {
      showToast('Update failed — server sync error', 'err');
      console.error('emp edit error:', err);
    }
    cancelEditEmp();
    fetchEmpDetails();
  };
  // ADDED: Delete handler with confirmation
  const deleteEmp = async (e, ctor) => {
    if (!window.confirm(`Delete this employee record?\n\n${e.name||'(no name)'} · ${e.id} · +${e.extra||0} extra tokens`)) return;
    if (!e.recordId) {
      // Optimistic local-only removal if no DB id (legacy/optimistic row)
      setEmpRecords(prev => {
        const list = (prev[ctor]||[]).filter(row =>
          !(row.id===e.id && row.date===e.date && (row.shift||'DAY')===(e.shift||'DAY'))
        );
        return { ...prev, [ctor]: list };
      });
      showToast('Removed locally', 'warn');
      return;
    }
    try {
      await axios.delete(`${PPC_API_URL}/papa.php?action=emp-tokens&id=${e.recordId}`, auth());
      showToast('🗑 Deleted');
      fetchEmpDetails();
    } catch (err) {
      showToast('Delete failed', 'err');
      console.error('emp delete error:', err);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div className="pw">

        {/* Header */}
        <header className="pw-hdr">
          <div className="pw-hdr-l">
            <button className="pw-hbtn" onClick={() => navigate('/ppc')}>← PPC Home</button>
            <span className="pw-htitle">PAPA DASHBOARD</span>
            <span className="pw-hbadge">{isAdmin ? '👑 Admin' : '👤 '+(user?.username||'User')}</span>
          </div>
          <div className="pw-hdr-r">
            <input type="date" className="pw-hdate" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            <button className="pw-hbtn" onClick={handleExport}>📥 Export</button>
          </div>
        </header>

        {/* Nav */}
        <nav className="pw-nav">
          {[
            { key:'papa',   label:'🍤 Papa Dashboard',        cls:'ap', dot:'#ea580c' },
            { key:'report', label:'📊 Reports',               cls:'ar', dot:'#1565c0' },
            { key:'inc',    label:'🎯 Incentive Token Details',cls:'ai', dot:'#059669' },
          ].map(t => (
            <button key={t.key} className={`pw-ntab${activeTab===t.key?' '+t.cls:''}`}
              onClick={() => setActiveTab(t.key)}>
              <span style={{ width:8,height:8,borderRadius:'50%',background:t.dot,display:'inline-block' }} />
              {t.label}
            </button>
          ))}
        </nav>

        {/* ══ PAPA DASHBOARD TAB ══ */}
        {activeTab === 'papa' && (
          <div className="pw-main">
            <div>
              <span className="pw-chip-p">🍤 SECTION 1 — PAPA DASHBOARD</span>
              <div style={{ fontSize:17,fontWeight:700,marginTop:5 }}>Papa Dashboard</div>
              <div style={{ fontSize:12,color:'var(--muted)',marginTop:2 }}>
                Product processing entries — {papaPeriod==='daily'?fmtD(selectedDate):ppRange}
              </div>
            </div>
            <PeriodTabs value={papaPeriod} onChange={setPapaPeriod} range={ppRange} />
            {/* KPI Cards */}
            <div className="pw-stats">
              {[
                { l:'Total Lots', v:kpi.lots,               c:'#d97706', cls:'kpi-a' },
                { l:'Output Qty', v:kpi.hon.toFixed(2)+' KG',c:'#ea580c', cls:'kpi-o' },
                { l:'HL Qty',     v:kpi.hl.toFixed(2)+' KG', c:'#1565c0', cls:'kpi-b' },
                { l:'Yield %',    v:kpi.yld.toFixed(2)+'%',  c:kpi.yC,    cls:kpi.yld>=85?'kpi-g':kpi.yld>=70?'kpi-y':'kpi-r' },
                { l:'Associates', v:kpi.assoc,               c:'#059669', cls:'kpi-g' },
              ].map((s,i) => (
                <div key={i} className={`pw-sc ${s.cls}`}>
                  <div className="pw-sc-lbl" style={{ color:s.c }}>{s.l}</div>
                  <div className="pw-sc-val" style={{ color:s.c }}>{s.v}</div>
                </div>
              ))}
            </div>
            {/* Entries table */}
            <div className="pw-card">
              <div className="pw-chdr">
                <span className="pw-ctitle">📋 {papaPeriod==='daily'?"Today's":papaPeriod==='weekly'?"Weekly":"Monthly"} Entries — {papaPeriod==='daily'?fmtD(selectedDate):ppRange}</span>
                <div style={{ display:'flex',gap:8 }}>
                  <button className="pw-btn-add" onClick={openAdd}>+ Add Entry</button>
                  <button className="pw-btn-exp" onClick={handleExport}>📥 Export</button>
                </div>
              </div>
              <div className="pw-tw">
                <table className="pw-tbl">
                  <thead>
                    <tr>
                      <th className="tl" rowSpan={2}>Contractor</th>
                      <th rowSpan={2}>Date</th>
                      <th rowSpan={2}>Shift</th>
                      <th rowSpan={2} style={{ background:'rgba(5,150,105,.08)',color:'#065f46' }}>Product</th>
                      <th rowSpan={2} style={{ background:'rgba(5,150,105,.08)',color:'#065f46' }}>Grade</th>
                      <th colSpan={2} style={{ background:'rgba(21,101,192,.08)',color:'#1565c0' }}>Quantity (KG)</th>
                      <th colSpan={3} style={{ background:'rgba(22,163,74,.08)',color:'#14532d' }}>Associates</th>
                      <th rowSpan={2}>Tokens</th>
                      <th rowSpan={2}>Work Hrs</th>
                      <th rowSpan={2} style={{ background:'#f8f9fa' }}>Actions</th>
                    </tr>
                    <tr>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0',fontSize:9 }}>HL Qty</th>
                      <th style={{ background:'rgba(234,88,12,.08)',color:'#7c2d12',fontSize:9 }}>Output Qty</th>
                      <th style={{ background:'rgba(211,47,47,.08)',color:'#7f1d1d',fontSize:9 }}>Female Assoc</th>
                      <th style={{ background:'rgba(22,163,74,.08)',color:'#14532d',fontSize:9 }}>Supp. Male</th>
                      <th style={{ background:'rgba(22,163,74,.08)',color:'#14532d',fontSize:9 }}>Supp. Female</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={14} style={{ padding:32,textAlign:'center',color:'#9ca3af' }}>Loading…</td></tr>
                    ) : papaRows.length === 0 ? (
                      <tr><td colSpan={14}>
                        <div className="pw-empty">
                          <div style={{ fontSize:36,opacity:.4,marginBottom:10 }}>🍤</div>
                          <div style={{ fontWeight:600,marginBottom:5 }}>No entries found</div>
                          <div style={{ fontSize:12 }}>Click + Add Entry to get started</div>
                        </div>
                      </td></tr>
                    ) : papaRows.map(r => {
                      const hl     = rowHlQty(r);
                      const outQt  = rowOutputQty(r);
                      const fa     = rowFemale(r);
                      const sm     = rowSuppMale(r);
                      const sf     = rowSuppFemale(r);
                      const yld    = calcYield(hl, outQt);
                      const yc     = yld>=85?'vg':yld>=70?'va':'vr';
                      const date = r.entry_date || r.date || selectedDate;
                      const sh = rowShift(r);
                      return (
                        <tr key={r.id}>
                          <td className="tl">{r.contractor}</td>
                          <td className="pw-mono">{fmtD(date)}</td>
                          <td><span style={{ padding:'3px 8px',borderRadius:10,fontSize:10,fontWeight:700,background:sh==='DAY'?'#fffbeb':sh==='GENERAL'?'#f0fdf4':'#eff6ff',color:sh==='DAY'?'#92400e':sh==='GENERAL'?'#14532d':'#1565c0' }}>{sh}</span></td>
                          <td className="pw-mono">{r.product||'—'}</td>
                          <td className="pw-mono">{r.grade||'—'}</td>
                          <td><span className="vb">{hl.toFixed(2)}</span></td>
                          <td><span style={{ color:'#ea580c',fontWeight:600,fontFamily:'monospace' }}>{outQt.toFixed(2)}</span></td>
                          <td><span style={{ color:'#d32f2f',fontWeight:600 }}>{fa}</span></td>
                          <td className="pw-mono">{sm}</td>
                          <td className="pw-mono">{sf}</td>
                          <td><span className="va">{r.tokens||0}</span></td>
                          <td className="pw-mono">{r.working_hours||r.hrs||0}</td>
                          <td>
                            <div style={{ display:'flex',gap:4,justifyContent:'center' }}>
                              <button className="pw-bsm" style={{ background:'#1565c0' }} onClick={() => openEdit(r)}>EDIT</button>
                              <button className="pw-bsm" style={{ background:'#dc2626' }} onClick={() => openDel(r.id)}>DEL</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {papaRows.length > 0 && (
                    <tfoot>
                      <tr>
                        <td className="tl">TOTAL ({papaRows.length})</td>
                        <td /><td /><td /><td />
                        <td><span className="vb">{totals.hlQty.toFixed(2)}</span></td>
                        <td><span style={{ color:'#ea580c',fontWeight:600,fontFamily:'monospace' }}>{totals.outputQty.toFixed(2)}</span></td>
                        <td><span style={{ color:'#d32f2f',fontWeight:600 }}>{totals.femaleAssoc}</span></td>
                        <td className="pw-mono">{totals.suppMale}</td>
                        <td className="pw-mono">{totals.suppFemale}</td>
                        <td><span className="va">{totals.tokens}</span></td>
                        <td className="pw-mono">{totals.workHours}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══ REPORTS TAB ══ */}
        {/* Reports tab — replaced Turn 1 with PapaReportDashboard component */}
        {activeTab === 'report' && (
          <PapaReportDashboard embedded />
        )}

        {/* ══ INCENTIVE TOKEN DETAILS TAB ══ */}
        {activeTab === 'inc' && (() => {
          const selR    = activeCtor;
          const emps    = (empRecords[selR?.contractor||'']||[]).filter(e => e.date === selectedDate);
          const earners = emps.filter(e=>parseInt(e.extra||0)>0).sort((a,b)=>parseInt(b.extra)-parseInt(a.extra));
          const belowStd= emps.filter(e=>parseInt(e.extra||0)<=0).length;
          // Per-contractor incentive summary — EMPLOYEE-based (same model as the
          // employee table footer): assigned = eligible employees × standard,
          // achieved = Σ tokens done, incentive = Σ extra tokens. Excludes any
          // support associates (these are per-employee token earners).
          // Associates = this contractor's actual female associates doing token
          // work (support excluded). Standard = associates × 60. Incentive = Σ
          // entered extra tokens. Achieved = Standard + Extra.
          const cAssoc    = parseInt(selR?.femaleAssoc) || 0;
          const extra     = ctorExtra(selR?.contractor || '');
          const std       = cAssoc * STD_TOK;
          const cAchieved = std + extra;
          const pct       = std>0?Math.round(cAchieved/std*100):0;
          // FIX: flat cross-contractor list — SELECTED DATE ONLY (no history).
          const allFlat = [];
          const seenKey = new Set();
          Object.entries(empRecords).forEach(([ctor, list]) => {
            (list || []).forEach(e => {
              if (e.date !== selectedDate) return;     // only the selected date
              const key = `${e.id||''}|${e.date||''}|${e.shift||'DAY'}`;
              if (!e.id || seenKey.has(key)) return;
              seenKey.add(key);
              allFlat.push({ ...e, contractor: ctor });
            });
          });
          const allEarners = allFlat
            .filter(e => parseInt(e.extra||0) > 0)
            .filter(e => e.date === selectedDate)
            .filter(e => !incEmpShift || (e.shift||'DAY')    === incEmpShift)
            .filter(e => !incEmpCtor  || e.contractor        === incEmpCtor)
            .sort((a,b) => parseInt(b.extra) - parseInt(a.extra));
          const allDates     = [...new Set(allFlat.map(e => e.date).filter(Boolean))].sort().reverse();
          const allCtorsList = [...new Set(allFlat.map(e => e.contractor))].sort();
          // Top summary cards — computed from the SAME employee earner set as the
          // table footer, so cards, footer, contractor summary and export all match:
          //   Associates       = eligible employee count
          //   Tokens Assigned  = eligible employees × standard
          //   Tokens Achieved  = Σ employee tokens done
          //   Incentive Tokens = Σ employee extra tokens
          // Associates / Standard are based on the actual female associates doing
          // token work (support excluded); Incentive = Σ entered extra tokens;
          // Achieved = Standard + Extra. Same contractor set shown as the pills.
          const totA   = incRows.reduce((s,r)=>s+(parseInt(r.femaleAssoc)||0),0);
          const totStd = totA * STD_TOK;
          const totEx  = incRows.reduce((s,r)=>s+ctorExtra(r.contractor),0);
          const totTok = totStd + totEx;
          const rankG   = ['linear-gradient(135deg,#f59e0b,#d97706)','linear-gradient(135deg,#9ca3af,#6b7280)','linear-gradient(135deg,#cd7c3a,#b45309)'];
          return (
            <div className="pw-main">
              {/* Title row */}
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10 }}>
                <div>
                  <span className="pw-chip-g">🎯 SECTION 2 — INCENTIVE DETAILS</span>
                  <div style={{ fontSize:19,fontWeight:700,marginTop:5 }}>Incentive Token Details Dashboard</div>
                  <div style={{ fontSize:12,color:'var(--muted)',marginTop:2 }}>Per-employee extra token breakdown · Standard: {STD_TOK} tokens / associate / shift</div>
                </div>
                <button className="pw-btn-exp" onClick={exportInc} style={{ display:'flex',alignItems:'center',gap:6 }}>📊 Export CSV</button>
              </div>

              {/* Main card */}
              <div className="pw-inc-card2">

                {/* Gradient header */}
                <div className="pw-inc-grad-hdr">
                  <div>
                    <div style={{ color:'#fff',fontSize:14,fontWeight:700,display:'flex',alignItems:'center',gap:7 }}>🎯 Incentive Token Details</div>
                    <div style={{ color:'rgba(255,255,255,.78)',fontSize:11,marginTop:3 }}>Click a contractor below to view employee-level extra token breakdown</div>
                  </div>
                  <div className="pw-inc-std-badge">🎯 Standard: {STD_TOK} tokens / associate</div>
                </div>

                {/* Filter bar */}
                <div className="pw-inc-filter-bar">
                  <div>
                    <label style={{ display:'block',fontSize:10,fontWeight:700,color:'#065f46',textTransform:'uppercase',letterSpacing:.4,marginBottom:4 }}>SHOW</label>
                    <select className="pw-inc-filter-sel" value={incFilter} onChange={e=>setIncFilter(e.target.value)}>
                      <option value="">All Contractors</option>
                      <option value="inc">With Incentive Only</option>
                      <option value="none">No Incentive Only</option>
                    </select>
                  </div>
                  <button className="pw-inc-clear" onClick={()=>setIncFilter('')}>✕ Clear</button>
                </div>

                {/* Stats strip */}
                <div className="pw-stat-strip">
                  {[
                    { l:'🏢 Contractors',       v:incRows.length,                              c:'#1565c0' },
                    { l:'👷 Associates',          v:totA,                                         c:'#1565c0' },
                    { l:'📋 Tokens Assigned',     v:totStd.toLocaleString(),                     c:'#d97706' },
                    { l:'✅ Tokens Achieved',     v:totTok.toLocaleString(),                     c:'#1565c0' },
                    { l:'🎯 Incentive Tokens',    v:(totEx>0?'+':'')+totEx.toLocaleString(),    c:totEx>0?'#059669':'#9ca3af' },
                  ].map((s,i) => (
                    <div key={i} className="pw-stat-cell" style={{ borderRight:i<4?'1px solid #bbf7d0':'none' }}>
                      <div className="pw-stat-lbl">{s.l}</div>
                      <div className="pw-stat-val" style={{ color:s.c }}>{s.v}</div>
                    </div>
                  ))}
                </div>

                {/* Contractor buttons */}
                <div className="pw-ctor-zone">
                  <div className="pw-ctor-zone-lbl">🏢 Select Contractor to View Employee Details</div>
                  <div className="pw-ctor-btns-wrap">
                    {incRows.map(r => {
                      const ex      = ctorExtra(r.contractor);
                      const isAct   = (selIncCtor||activeCtor?.contractor) === r.contractor;
                      const nEarners= (empRecords[r.contractor]||[]).filter(e=>parseInt(e.extra||0)>0 && e.date===selectedDate).length;
                      return (
                        <button key={r.contractor} className={`pw-ctor-pill${isAct?' active':''}`}
                          onClick={() => setSelIncCtor(r.contractor)}>
                          <span style={{ width:9,height:9,borderRadius:'50%',background:ex>0?'#059669':'#9ca3af',flexShrink:0,display:'inline-block' }} />
                          {r.contractor}
                          {nEarners>0 && <span className="pw-ctor-earner">+{nEarners}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Detail panel */}
                {selR && (
                  <div className="pw-det-panel">
                    {/* Summary row */}
                    <div className="pw-det-summary">
                      <div className="pw-det-cell">
                        <div className="pw-det-lbl">🏢 Contractor</div>
                        <div className="pw-det-val" style={{ fontSize:15,color:'var(--text)' }}>{selR.contractor}</div>
                        <div className="pw-det-sub"><span className="pw-day-pill">DAY</span></div>
                      </div>
                      <div className="pw-det-cell">
                        <div className="pw-det-lbl">👷 Associates</div>
                        <div className="pw-det-val" style={{ color:'#1565c0' }}>{cAssoc}</div>
                        <div className="pw-det-sub" style={{ color:'var(--muted)' }}>{emps.length} records</div>
                      </div>
                      <div className="pw-det-cell">
                        <div className="pw-det-lbl">📋 Tokens Assigned</div>
                        <div className="pw-det-val" style={{ color:'#d97706' }}>{std.toLocaleString()}</div>
                        <div className="pw-det-sub" style={{ color:'var(--muted)' }}>{cAssoc}×{STD_TOK}</div>
                      </div>
                      <div className="pw-det-cell">
                        <div className="pw-det-lbl">✅ Tokens Achieved</div>
                        <div className="pw-det-val" style={{ color:'#1565c0' }}>{cAchieved.toLocaleString()}</div>
                        <div className="pw-det-sub" style={{ color:pct>=100?'#059669':'#d97706' }}>{pct}% of standard</div>
                      </div>
                      <div className="pw-det-cell">
                        <div className="pw-det-lbl">🎯 Extra Tokens</div>
                        <div style={{ marginTop:4 }}>
                          {extra>0
                            ? <span className="pw-extra-pill">+{extra}</span>
                            : <span className="pw-no-inc-pill">No incentive</span>
                          }
                        </div>
                        <div className="pw-det-sub" style={{ color:extra>0?'#059669':'#9ca3af' }}>
                          {extra>0 ? `${earners.length} employees earned` : 'Standard not exceeded'}
                        </div>
                      </div>
                    </div>

                    {/* Toggle button — FIX: ungated from extra>0 so it shows even when aggregate below standard */}
                    <button
                      className={showEmps ? 'pw-hide-tog' : 'pw-show-tog'}
                      onClick={() => setShowEmps(v => !v)}>
                      {showEmps ? '🙈 Hide Incentive Employee Details' : '👁 Show Incentive Employee Details'}
                    </button>

                    {/* Employee table — FIX: ungated; uses cross-contractor filtered list */}
                    {showEmps && (
                      <>
                        {/* FIX: filter row mirrors Bravo */}
                        <div style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap', marginTop:12, marginBottom:8 }}>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:.5 }}>📅 Date</label>
                            <select value={incEmpDate} onChange={e=>setIncEmpDate(e.target.value)}
                              style={{ padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontFamily:'inherit', fontSize:12, background:'#fff' }}>
                              <option value="">All Dates</option>
                              {allDates.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:.5 }}>🌓 Shift</label>
                            <select value={incEmpShift} onChange={e=>setIncEmpShift(e.target.value)}
                              style={{ padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontFamily:'inherit', fontSize:12, background:'#fff' }}>
                              <option value="">All Shifts</option>
                              <option value="DAY">DAY</option>
                              <option value="NIGHT">NIGHT</option>
                              <option value="GENERAL">GENERAL</option>
                            </select>
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:.5 }}>👷 Contractor</label>
                            <select value={incEmpCtor} onChange={e=>setIncEmpCtor(e.target.value)}
                              style={{ padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontFamily:'inherit', fontSize:12, background:'#fff' }}>
                              <option value="">All Contractors</option>
                              {allCtorsList.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <button
                            onClick={() => { setIncEmpDate(''); setIncEmpShift(''); setIncEmpCtor(''); }}
                            style={{ padding:'7px 14px', border:'1px solid #d1d5db', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, fontFamily:'inherit', color:'var(--muted)' }}>
                            ✕ Reset Filters
                          </button>
                          <div style={{ marginLeft:'auto', fontSize:11, color:'var(--muted)' }}>
                            {allEarners.length} earner{allEarners.length!==1?'s':''}
                          </div>
                        </div>

                        <div className="pw-emp-info-bar">
                          ℹ️ Only employees who completed&nbsp;<strong>more than {STD_TOK} tokens</strong>.
                        </div>
                        <div className="pw-emp-section">
                          <table className="pw-emp-full-tbl">
                            <thead>
                              <tr>
                                <th style={{ width:44,textAlign:'center' }}>#</th>
                                <th>Employee Name</th>
                                <th>Employee ID</th>
                                <th className="ar">Tokens Done</th>
                                <th className="ar">Standard ({STD_TOK})</th>
                                <th className="ar">Extra Tokens</th>
                                <th style={{ minWidth:160 }}>Progress</th>
                                <th style={{ width:120,textAlign:'center' }}>Actions</th>{/* ADDED: Action column */}
                              </tr>
                            </thead>
                            <tbody>
                              {allEarners.length === 0 && (
                                <tr><td colSpan={8} style={{ padding:24,textAlign:'center',color:'#9ca3af' }}>No employees exceeded the token limit</td></tr>
                              )}
                              {allEarners.map((e,i) => {
                                const ex2   = parseInt(e.extra||0);
                                const pctBar= Math.min(100,Math.round((ex2/STD_TOK)*100));
                                // ADDED: inline edit detection
                                const rowKey  = `${e.contractor}|${e.id}|${e.date||''}|${e.shift||'DAY'}`;
                                const isEdit  = editEmpKey === rowKey;
                                return (
                                  <tr key={`${e.id}-${e.date}-${e.shift}-${i}`}
                                      style={isEdit ? { background:'#fffbeb', borderLeft:'4px solid #d97706' } : undefined}>
                                    <td style={{ textAlign:'center' }}>
                                      <span className="pw-rank-circle"
                                        style={{ background:rankG[i]||'linear-gradient(135deg,#d32f2f,#1565c0)' }}>
                                        {i+1}
                                      </span>
                                    </td>
                                    {/* ADDED: editable Name */}
                                    <td style={{ fontWeight:600 }}>
                                      {isEdit ? (
                                        <input type="text" value={editEmpDraft.name}
                                          onChange={ev=>setEditEmpDraft(d=>({ ...d, name:ev.target.value }))}
                                          style={{ width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:5,fontSize:11,fontFamily:'inherit' }} />
                                      ) : e.name}
                                    </td>
                                    {/* ADDED: editable ID */}
                                    <td style={{ fontFamily:'DM Mono,monospace',fontSize:11,color:'var(--muted)' }}>
                                      {isEdit ? (
                                        <input type="text" value={editEmpDraft.id}
                                          onChange={ev=>setEditEmpDraft(d=>({ ...d, id:ev.target.value }))}
                                          style={{ width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:5,fontSize:11,fontFamily:'DM Mono,monospace' }} />
                                      ) : e.id}
                                    </td>
                                    {/* ADDED: editable Tokens */}
                                    <td className="ar" style={{ fontWeight:700,color:'#1565c0',fontFamily:'DM Mono,monospace' }}>
                                      {isEdit ? (
                                        <input type="number" min="0" value={editEmpDraft.tokens}
                                          onChange={ev=>setEditEmpDraft(d=>({ ...d, tokens:ev.target.value }))}
                                          style={{ width:'100%',padding:'4px 8px',border:'1px solid #d1d5db',borderRadius:5,fontSize:11,textAlign:'right',fontFamily:'DM Mono,monospace' }} />
                                      ) : (e.tokens||0)}
                                    </td>
                                    <td className="ar" style={{ color:'#9ca3af',fontFamily:'DM Mono,monospace' }}>{STD_TOK}</td>
                                    <td className="ar"><span className="pw-ext-tag2">+{ex2}</span></td>
                                    <td>
                                      {isEdit ? (
                                        <span style={{ color:'#9ca3af',fontStyle:'italic',fontSize:10 }}>Editing…</span>
                                      ) : (
                                        <div className="pw-prog-wrap">
                                          <div className="pw-prog-bar2">
                                            <div className="pw-prog-fill2" style={{ width:`${pctBar}%` }} />
                                          </div>
                                          <span style={{ fontFamily:'DM Mono,monospace',fontSize:11,color:'#059669',fontWeight:700,minWidth:32 }}>+{ex2}</span>
                                        </div>
                                      )}
                                    </td>
                                    {/* ADDED: Action cell */}
                                    <td style={{ textAlign:'center' }}>
                                      {isEdit ? (
                                        <div style={{ display:'inline-flex',gap:6 }}>
                                          <button onClick={()=>saveEditEmp(e, e.contractor)}
                                            style={{ padding:'4px 10px',borderRadius:5,border:'none',background:'#059669',color:'#fff',fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}>SAVE</button>
                                          <button onClick={cancelEditEmp}
                                            style={{ padding:'4px 10px',borderRadius:5,border:'1px solid #d1d5db',background:'#fff',color:'#374151',fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}>CANCEL</button>
                                        </div>
                                      ) : (
                                        <div style={{ display:'inline-flex',gap:6 }}>
                                          <button onClick={()=>startEditEmp(e, e.contractor)}
                                            style={{ padding:'4px 10px',borderRadius:5,border:'none',background:'#1565c0',color:'#fff',fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}>EDIT</button>
                                          <button onClick={()=>deleteEmp(e, e.contractor)}
                                            style={{ padding:'4px 10px',borderRadius:5,border:'none',background:'#d32f2f',color:'#fff',fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit' }}>DEL</button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {allEarners.length > 0 && (
                              <tfoot>
                                <tr className="pw-emp-tbl-foot">
                                  <td colSpan={4} style={{ padding:'10px 14px',fontWeight:700 }}>
                                    TOTAL — {allEarners.length} employee{allEarners.length!==1?'s':''} above standard
                                  </td>
                                  <td className="ar" style={{ padding:'10px 14px',color:'#9ca3af',fontFamily:'DM Mono,monospace',fontWeight:700 }}>
                                    {allEarners.length*STD_TOK}
                                  </td>
                                  <td className="ar" style={{ padding:'10px 14px' }}>
                                    <span className="pw-ext-tag2">+{allEarners.reduce((s,e)=>s+(parseInt(e.extra)||0),0)}</span>
                                  </td>
                                  <td />
                                  <td />{/* ADDED: empty cell aligning with Action column */}
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      </>
                    )}

                    {/* No-incentive aggregate note (info only — does NOT hide the table) */}
                    {extra === 0 && allEarners.length === 0 && (
                      <div className="pw-no-inc-state">
                        <div style={{ fontSize:40,opacity:.3,marginBottom:12 }}>📊</div>
                        <div style={{ fontSize:14,fontWeight:600,color:'var(--muted)' }}>{selR.contractor} — No Incentive Tokens This Period</div>
                        <div className="pw-shortfall">⚠️ Shortfall: {std-selR.tokens} tokens below standard</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

                {/* ══ ADD / EDIT MODAL ══ */}
        {showModal && (
          <div className="pw-overlay" onClick={e => e.target.classList.contains('pw-overlay') && closeModal()}>
            <div className="pw-mbox" onClick={e => e.stopPropagation()}>
              <div className="pw-mhdr">
                <div>
                  <div className="pw-mtitle">{editId ? '✏️ Edit Entry' : '➕ Add Entry'} — Papa Dashboard</div>
                  <div className="pw-msub">{fCtor||'—'} · {fmtD(selectedDate)} · {fShift} shift</div>
                </div>
                <button className="pw-mcls" onClick={closeModal}>×</button>
              </div>
              <div className="pw-mbody">
                {/* Basic Info */}
                <div className="pw-fsec-o">
                  <div className="pw-flbl-o">📅 Basic Info</div>
                  <div className="pw-mg3">
                    <div className="pw-ff">
                      <label>Entry Date</label>
                      <input type="date" value={selectedDate} readOnly className="ro" style={{ fontFamily:'inherit' }} />
                      <span style={{ fontSize:9,color:'#9ca3af',marginTop:3,display:'block' }}>Set from header date picker</span>
                    </div>
                    <div className="pw-ff">
                      <label>Contractor</label>
                      <select value={fCtor} onChange={e=>setFCtor(e.target.value)} style={{ fontFamily:'inherit' }}>
                        <option value="">— Select —</option>
                        {contractors.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="pw-ff">
                      <label>Shift *</label>
                      <div className="pw-sbtn-wrap">
                        {['DAY','GENERAL','NIGHT'].map(s => (
                          <button key={s} type="button" className={`pw-sbtn${fShift===s?' on':''}`} onClick={()=>setFShift(s)}>
                            {s==='DAY'?'☀️':s==='GENERAL'?'🔄':'🌙'} {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Associates & Tokens */}
                <div className="pw-fsec-o">
                  <div className="pw-flbl-o">👷 Associates &amp; Tokens</div>
                  <div className="pw-mg5">
                    <div className="pw-ff"><label>Tokens</label><input type="number" value={fTokens} onChange={e=>setFTokens(e.target.value)} min={0} placeholder="e.g. 140" onWheel={e=>e.target.blur()} /></div>
                    <div className="pw-ff"><label>Assoc Earned Incentive</label><input type="number" value={fAssocInc} onChange={e=>setFAssocInc(e.target.value)} min={0} placeholder="No." onWheel={e=>e.target.blur()} /></div>
                    <div className="pw-ff"><label>Female Associates</label><input type="number" value={fFemaleAssoc} onChange={e=>setFFemaleAssoc(e.target.value)} min={0} placeholder="No." onWheel={e=>e.target.blur()} style={{ borderColor:'#fca5a5',background:'#fef2f2' }} /></div>
                    <div className="pw-ff"><label>Supp. Male</label><input type="number" value={fMale} onChange={e=>setFMale(e.target.value)} min={0} placeholder="No." onWheel={e=>e.target.blur()} /></div>
                    <div className="pw-ff"><label>Supp. Female</label><input type="number" value={fFemale} onChange={e=>setFFemale(e.target.value)} min={0} placeholder="No." onWheel={e=>e.target.blur()} /></div>
                    <div className="pw-ff"><label>Working Hours</label><input type="number" value={fHrs} onChange={e=>setFHrs(e.target.value)} min={0.5} step={0.5} placeholder="e.g. 8" onWheel={e=>e.target.blur()} /></div>
                  </div>
                </div>
                {/* Products */}
                <div className="pw-fsec-g">
                  <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
                    <div className="pw-flbl-g" style={{ marginBottom:0 }}>📦 Products &amp; Quantities</div>
                    <button type="button" className="pw-add-line" onClick={addLine}>+ Add More</button>
                  </div>
                  {lineItems.map((line, idx) => (
                    <div key={idx} className="pw-line-item">
                      <div className="pw-ff" style={{ flex:1.4 }}>
                        <label>Product</label>
                        <select value={line.product} onChange={e=>setLine(idx,'product',e.target.value)} style={{ fontFamily:'inherit' }}>
                          <option value="">Select</option>
                          {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="pw-ff" style={{ flex:1 }}>
                        <label>Grade</label>
                        <select value={line.grade} onChange={e=>setLine(idx,'grade',e.target.value)} style={{ fontFamily:'inherit' }}>
                          <option value="">Select</option>
                          {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                      <div className="pw-ff" style={{ flex:1 }}>
                        <label>HL Qty (KG)</label>
                        <input type="number" value={line.hl_qty} onChange={e=>setLine(idx,'hl_qty',e.target.value)} min={0} step={0.01} placeholder="0.00" onWheel={e=>e.target.blur()} />
                      </div>
                      <div className="pw-ff" style={{ flex:1 }}>
                        <label>Output Qty (KG)</label>
                        <input type="number" value={line.output_qty} onChange={e=>setLine(idx,'output_qty',e.target.value)} min={0} step={0.01} placeholder="0.00" style={{ borderColor:'#fdba74',background:'#fff7ed' }} onWheel={e=>e.target.blur()} />
                      </div>
                      {idx > 0 && <button type="button" className="pw-rm-line" onClick={()=>remLine(idx)}>✕</button>}
                    </div>
                  ))}
                </div>
                {/* Auto Calc */}
                <div className="pw-fsec-c">
                  <div className="pw-flbl-c">🔢 Auto Calculations</div>
                  <div className="pw-calc-grid">
                    <div className="pw-calc-cell"><div className="pw-calc-lbl">Total Assoc</div><div className="pw-calc-val" style={{ color:'#059669' }}>{calcAssoc}</div></div>
                    <div className="pw-calc-cell"><div className="pw-calc-lbl">HL Qty</div><div className="pw-calc-val" style={{ color:'#1565c0' }}>{calcHL.toFixed(2)}</div></div>
                    <div className="pw-calc-cell"><div className="pw-calc-lbl">Output Qty</div><div className="pw-calc-val" style={{ color:'#ea580c' }}>{calcOutput.toFixed(2)}</div></div>
                    <div className="pw-calc-cell">
                      <div className="pw-calc-lbl">Yield %</div>
                      <div className="pw-calc-val" style={{ color:calcYld>=85?'#059669':calcYld>=70?'#d97706':'#d32f2f' }}>
                        {calcHL>0 ? calcYld.toFixed(2)+'%' : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

                {/* Extra Tokens toggle */}
                <div className="pw-fsec-c" style={{ background:'#eff6ff',border:'1px solid #bfdbfe' }}>
                  <div className="pw-flbl-c" style={{ color:'#1565c0' }}>🎯 Incentive / Extra Tokens</div>
                  <div className="pw-et-row">
                    <span className="pw-et-lbl">Extra Tokens? <span style={{ color:'#dc2626' }}>*</span></span>
                    <button type="button"
                      className={`pw-yn-btn pw-yn-yes${extraTokensOn?' on':''}`}
                      onClick={() => setExtraTokensOn(true)}>
                      ✅ Yes
                    </button>
                    <button type="button"
                      className={`pw-yn-btn pw-yn-no${!extraTokensOn?' on':''}`}
                      onClick={() => setExtraTokensOn(false)}>
                      ✕ No
                    </button>
                  </div>
                </div>

                {/* Incentive Section */}
                {extraTokensOn && (() => {
                  const totalExtra = empRows.reduce((s,r) => s + (parseInt(r.extra)||0), 0);
                  const assoc      = calcAssoc;
                  const stdToks    = assoc * EXTRA_TOK_STD;
                  const excess     = assoc > 0 ? totalExtra / assoc : 0;
                  const hasInc     = totalExtra > stdToks;
                  return (
                    <div className="pw-inc-wrap">
                      <div className="pw-inc-top">
                        <span style={{ fontSize:13,fontWeight:700,color:'#065f46' }}>📋 Contractor-wise Incentive Token Summary</span>
                        <span className="pw-new-badge">New</span>
                      </div>
                      <div className="pw-inc-info">ℹ️ Toggle <strong>Yes</strong> to enter employee-level extra token details (employees above {EXTRA_TOK_STD} tokens).</div>
                      <div className="pw-inc-body">
                        <div style={{ fontSize:11,fontWeight:700,color:'#065f46',marginBottom:8,paddingLeft:8,borderLeft:'3px solid #059669' }}>
                          Employee Extra Token Details <span style={{ fontWeight:400,color:'var(--muted)' }}>(above {EXTRA_TOK_STD} tokens)</span>
                        </div>
                        <table className="pw-emp-tbl">
                          <thead>
                            <tr>
                              <th style={{ width:34,textAlign:'center' }}>#</th>
                              <th>Employee Name</th>
                              <th>Employee ID</th>
                              <th style={{ textAlign:'right' }}>Extra Tokens</th>
                              <th style={{ width:34 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {empRows.map((row, idx) => (
                              <tr key={row.id}>
                                <td style={{ textAlign:'center',color:'var(--muted)',fontSize:11,width:34 }}>{idx+1}</td>
                                <td>
                                  <input className="pw-tinp" type="text" value={row.name} placeholder="Employee name"
                                    onChange={e => setEmpRows(prev => prev.map((r,i) => i===idx ? {...r, name:e.target.value} : r))} />
                                </td>
                                <td>
                                  <input className="pw-tinp" type="text" value={row.empId} placeholder="EMP-001"
                                    onChange={e => setEmpRows(prev => prev.map((r,i) => i===idx ? {...r, empId:e.target.value} : r))} />
                                </td>
                                <td style={{ textAlign:'right' }}>
                                  <input className="pw-tinp pw-tok-inp" type="number" min={0} value={row.extra} placeholder="0"
                                    onChange={e => {
                                      const v = e.target.value;
                                      if (v !== '' && parseInt(v) < 0) return;
                                      setEmpRows(prev => prev.map((r,i) => i===idx ? {...r, extra:v} : r));
                                    }}
                                    onWheel={e => e.target.blur()} />
                                </td>
                                <td style={{ textAlign:'center',width:34 }}>
                                  {empRows.length > 1 && (
                                    <button className="pw-del-x"
                                      onClick={() => setEmpRows(prev => prev.filter((_,i) => i!==idx))}>
                                      ×
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <button className="pw-add-emp"
                          onClick={() => {
                            setEmpRows(prev => [...prev, { id:nextEmpId, name:'', empId:`EMP-${String(nextEmpId).padStart(3,'0')}`, extra:'' }]);
                            setNextEmpId(n => n+1);
                          }}>
                          + Add Employee Record
                        </button>
                        {/* Total Extra Tokens */}
                        <div className="pw-tot-bar">
                          <span>Total Extra Tokens</span>
                          <span style={{ fontFamily:'DM Mono,monospace',fontSize:16 }}>{totalExtra}</span>
                        </div>
                        {/* Token Incentive Calculation */}
                        <div className="pw-tc-wrap">
                          <div style={{ fontSize:11,fontWeight:700,color:'#ea580c',marginBottom:9,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
                            🎯 Token Incentive Calculation
                            <span style={{ background:'#fff7ed',border:'1px solid #fdba74',color:'#7c2d12',fontSize:10,fontWeight:700,padding:'2px 9px',borderRadius:10 }}>
                              Standard: {EXTRA_TOK_STD} / associate
                            </span>
                          </div>
                          <div className="pw-tc-grid">
                            <div className="pw-tc-cell"><div className="pw-tc-lbl">Total Associates</div><div className="pw-tc-val" style={{ color:'#1565c0' }}>{assoc}</div></div>
                            <div className="pw-tc-cell"><div className="pw-tc-lbl">Standard Tokens</div><div className="pw-tc-val" style={{ color:'#d97706' }}>{stdToks}</div></div>
                            <div className="pw-tc-cell"><div className="pw-tc-lbl">Incentive Tokens</div><div className="pw-tc-val" style={{ color:'#1565c0' }}>{totalExtra}</div></div>
                            <div className="pw-tc-cell"><div className="pw-tc-lbl">Excess / Associate</div><div className="pw-tc-val" style={{ color:'#ea580c' }}>{excess.toFixed(2)}</div></div>
                          </div>
                          {!hasInc ? (
                            <div className="pw-msg-none">⚠️ No incentive — tokens ({totalExtra}) did not exceed standard ({stdToks})</div>
                          ) : (
                            <div className="pw-msg-inc">✅ Incentive earned! Extra {totalExtra - stdToks} tokens above standard. Excess/associate: {excess.toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              <div className="pw-mfoot">
                <button className="pw-btn-cancel" onClick={closeModal}>Cancel</button>
                <button className="pw-btn-save" onClick={handleSave}>
                  {editId ? '💾 Update Entry' : '💾 Save Entry'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ DELETE MODAL ══ */}
        {showDel && delId && (
          <div className="pw-overlay" onClick={e => e.target.classList.contains('pw-overlay') && closeDel()}>
            <div className="pw-dbox" onClick={e => e.stopPropagation()}>
              <div style={{ fontSize:44,marginBottom:14 }}>🗑️</div>
              <div style={{ fontSize:17,fontWeight:700,color:'#d32f2f',marginBottom:6 }}>Delete Entry?</div>
              <div style={{ fontSize:12,color:'var(--muted)',marginBottom:4 }}>You are about to delete:</div>
              <div style={{ fontSize:14,fontWeight:700,marginBottom:14 }}>
                {(() => { const e=entries.find(r=>r.id===delId); return e?`${e.contractor} — ${e.shift} — ${fmtD(e.entry_date||e.date||selectedDate)}`:delId; })()}
              </div>
              <div style={{ fontSize:11,color:'#d32f2f',background:'#fff5f5',border:'1px solid #fecaca',borderRadius:7,padding:'9px 12px',marginBottom:18,textAlign:'left' }}>
                ⚠️ This action cannot be undone.
              </div>
              <div style={{ display:'flex',gap:10 }}>
                <button className="pw-btn-cancel" onClick={closeDel}>Cancel</button>
                <button onClick={confirmDel} style={{ flex:2,padding:11,borderRadius:8,border:'none',background:'linear-gradient(135deg,#dc2626,#ef4444)',color:'#fff',fontFamily:'inherit',fontSize:13,fontWeight:700,cursor:'pointer' }}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast.show && <div className={`pw-toast ${toast.type}`}>{toast.msg}</div>}
      </div>
    </>
  );
}
